/**
 * bot-channels-field.tsx — Ô "Kênh phụ trách" của một nhân viên AI (DÙNG CHUNG: màn nhân viên AI, luồng tạo mới).
 *
 * Danh sách kênh đã kết nối có ô tick; kênh đang giao riêng cho nhân viên AI khác ghi "Đang giao cho X" (tick = chuyển sang
 * nhân viên này — mỗi kênh chỉ một nhân viên AI). Nhân viên AI mặc định: dòng đầu "Mọi kênh chưa giao cho ai" (luôn bật)
 * + các kênh giao riêng cho nó (giữ kênh kể cả khi sau này kênh được giao cho người khác).
 *
 * "Kết nối thêm kênh" mở màn Kết nối ở TAB MỚI (màn này giữ nguyên); "Tải lại" chỉ tải lại danh sách kênh + trạng thái
 * kết nối (cũng tự làm mới khi quay lại tab).
 *
 * Props: `botId` (null khi tạo mới), `isDefault`, `value` (id kênh giao riêng), `onChange(ids)`, `disabled`.
 */
import { Inbox, PlugZap, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { PlatformTag } from '@/components/shared/platform-mark'
import { ChannelChips } from '@/components/shared/channel-chips'
import { useChannelPicker } from '@/hooks/use-channel-picker'
import { useAiBots, useChannelOverrides, type ChannelInfo } from '@/hooks/use-ai'
import { cn } from '@/lib/utils'
import { channelRows, toggleId } from './bot-channels-lib'
import { OpenInNewTab } from './open-in-new-tab'
import { ReloadButton } from './reload-button'
import { CONNECT_CHANNELS_PATH } from './open-target-lib'

const NO_CHANNELS: ChannelInfo[] = []

export function BotChannelsField({ botId, isDefault, value, onChange, disabled }: {
  botId: string | null
  isDefault: boolean
  value: string[]
  onChange: (ids: string[]) => void
  disabled?: boolean
}) {
  const { t } = useTranslation('aiComp')
  const channelsQ = useChannelOverrides()
  const botsQ = useAiBots()
  const { data: channelData, isLoading } = channelsQ
  const { data: botsData } = botsQ
  const reload = () => Promise.all([channelsQ.refetch(), botsQ.refetch()])
  const channels = channelData?.channels ?? NO_CHANNELS
  const picker = useChannelPicker(channels)
  const bots = botsData?.bots ?? []
  const rows = channelRows(picker.matched, bots, { id: botId ?? '', channelAccountIds: value })
  const unassigned = channelRows(channels, bots, { id: botId ?? '', channelAccountIds: value }).filter((r) => r.viaDefault).length
  const defaultBot = bots.find((b) => b.isDefault ?? b.allChannels)

  if (!isLoading && channels.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-center text-sm">
        <Inbox className="mx-auto h-5 w-5 text-muted-foreground" />
        <p className="mt-1 font-medium">{t('botChannels.emptyTitle')}</p>
        <p className="text-xs text-muted-foreground">{t('botChannels.emptyHint')}</p>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-1">
          <OpenInNewTab to={CONNECT_CHANNELS_PATH} variant="outline" size="sm"><PlugZap className="h-3.5 w-3.5" /> {t('botChannels.connect')}</OpenInNewTab>
          <ReloadButton onReload={reload} what={t('botChannels.what')} />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-end gap-1">
        <ReloadButton onReload={reload} what={t('botChannels.what')} />
        <OpenInNewTab to={CONNECT_CHANNELS_PATH} variant="outline" size="sm" title={t('botChannels.openConnect')}>
          <PlugZap className="h-3.5 w-3.5" /> {t('botChannels.connectMore')}
        </OpenInNewTab>
      </div>
      {isDefault && (
        <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
          <input type="checkbox" checked readOnly disabled className="mt-1" aria-label={t('botChannels.allUnassigned')} />
          <span className="min-w-0">
            <span className="font-medium">{t('botChannels.allUnassigned')}</span>
            <span className="ml-1 text-xs text-muted-foreground">({t('botChannels.unassignedNow', { count: unassigned })})</span>
            <span className="block text-xs text-muted-foreground">{t('botChannels.defaultHint')}</span>
          </span>
        </div>
      )}
      {(picker.showSearch || picker.showChips) && (
        <div className="flex flex-wrap items-center gap-2">
          {picker.showSearch && (
            <div className="relative min-w-[180px] flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input value={picker.query} onChange={(e) => picker.setQuery(e.target.value)} placeholder={t('botChannels.search')} className="h-8 pl-8 text-xs" />
            </div>
          )}
          {picker.showChips && <ChannelChips groups={picker.groups} value={picker.channel} onChange={picker.setChannel} />}
        </div>
      )}
      <ul className="max-h-80 divide-y overflow-y-auto rounded-lg border">
        {rows.map((r) => (
          <li key={r.id}>
            <label className={cn('flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-muted/40', r.checked && 'bg-primary/5', disabled && 'cursor-not-allowed opacity-70')}>
              <input type="checkbox" checked={r.checked} onChange={(e) => onChange(toggleId(value, r.id, e.target.checked))} disabled={disabled} />
              <span className="min-w-0 flex-1 truncate">{r.name}</span>
              {r.ownerName && !r.checked && <Badge variant="outline" className="shrink-0 text-[10px] font-normal">{t('botChannels.assignedTo', { name: r.ownerName })}</Badge>}
              {r.ownerName && r.checked && <Badge variant="warning" className="shrink-0 text-[10px] font-normal">{t('botChannels.willMoveFrom', { name: r.ownerName })}</Badge>}
              {r.viaDefault && !isDefault && defaultBot && <span className="shrink-0 text-[10px] text-muted-foreground">{t('botChannels.inCharge', { name: defaultBot.name })}</span>}
              <PlatformTag platform={r.platform} className="shrink-0" />
            </label>
          </li>
        ))}
        {rows.length === 0 && <li className="p-3 text-center text-xs text-muted-foreground">{isLoading ? t('botChannels.loading') : t('botChannels.noMatch')}</li>}
      </ul>
      <p className="text-xs text-muted-foreground">
        {value.length > 0 ? t('botChannels.ownCount', { count: value.length }) : isDefault ? t('botChannels.ownNone') : t('botChannels.noneChosen')}{' '}
        {t('botChannels.onePerChannel')}
      </p>
    </div>
  )
}
